import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';

// Canonical cuisine list — order matters (curated, not alphabetical). "Other"
// is intentionally last so owners default to a real cuisine rather than the
// catch-all when scanning the chip cloud.
export const CUISINE_OPTIONS = [
  'American', 'Brazilian', 'Italian', 'Pizza', 'Mexican', 'Japanese', 'Sushi',
  'Chinese', 'Korean', 'Vietnamese', 'Thai', 'Indian', 'Mediterranean', 'BBQ',
  'Burgers', 'Seafood', 'Breakfast', 'Bakery', 'Coffee', 'Desserts',
  'Vegan', 'Vegetarian', 'Healthy', 'Latin', 'Caribbean', 'Other',
];

/**
 * CuisineTypeSelector — multi-select chip cloud.
 *
 * Used in Onboarding Setup + Dashboard Settings. The legacy single
 * `cuisine_type` string is mapped to a one-element array by the backend
 * mirror, so this component is the single UI for both old and new docs.
 *
 * Props:
 *   value      — string[]                (selected cuisines)
 *   onChange   — (next: string[]) => void
 *   variant?   — 'light' | 'dark'        (default 'dark')
 *   max?       — number (default 5)      (silently caps additions)
 *   testid?    — string                  (data-testid prefix)
 */
const CuisineTypeSelector = ({
  value = [],
  onChange,
  variant = 'dark',
  max = 5,
  testid = 'cuisine-types',
}) => {
  const dark = variant === 'dark';
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const isSel = (c) => selected.some((s) => s.toLowerCase() === c.toLowerCase());
  const [custom, setCustom] = useState('');

  const toggle = (c) => {
    if (isSel(c)) {
      onChange(selected.filter((s) => s.toLowerCase() !== c.toLowerCase()));
    } else if (selected.length < max) {
      onChange([...selected, c]);
    }
  };

  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (selected.length >= max) { setCustom(''); return; }
    if (!isSel(v)) onChange([...selected, v]);
    setCustom('');
  };

  // Tailwind class helpers — keep selected vs idle vs disabled visually
  // distinct on both light (Settings) and dark (Onboarding) surfaces.
  const chipCls = (sel) => {
    if (dark) {
      return sel
        ? 'bg-yellow-400 text-black border-yellow-400 hover:bg-yellow-300'
        : 'bg-white/[0.04] text-white/85 border-white/12 hover:border-white/35';
    }
    return sel
      ? 'bg-gold-500 text-white border-gold-500 hover:bg-gold-600'
      : 'bg-white text-slate-700 border-slate-200 hover:border-gold-400';
  };

  return (
    <div data-testid={testid}>
      {/* Selected pills row — shows count + removable chips first */}
      {selected.length > 0 && (
        <div
          className={`flex flex-wrap gap-1.5 mb-3 ${dark ? '' : 'text-slate-700'}`}
          data-testid={`${testid}-selected`}
        >
          {selected.map((c) => (
            <span
              key={c}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-bold ${
                dark ? 'bg-yellow-400 text-black' : 'bg-gold-500 text-white'
              }`}
              data-testid={`${testid}-selected-${c.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {c}
              <button
                type="button"
                onClick={() => toggle(c)}
                aria-label={`Remove ${c}`}
                className="hover:bg-black/10 rounded-full p-0.5 -mr-1 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Full chip cloud */}
      <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-options`}>
        {CUISINE_OPTIONS.map((c) => {
          const sel = isSel(c);
          const disabled = !sel && selected.length >= max;
          return (
            <button
              key={c}
              type="button"
              onClick={() => !disabled && toggle(c)}
              disabled={disabled}
              data-testid={`${testid}-option-${c.toLowerCase().replace(/\s+/g, '-')}`}
              aria-pressed={sel}
              className={`px-3 py-1.5 rounded-full border text-[12px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${chipCls(sel)}`}
            >
              {c}
            </button>
          );
        })}
      </div>

      {/* Inline custom-add — for niche cuisines not in the canonical list */}
      <div className="flex items-center gap-2 mt-3">
        <input
          type="text"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          placeholder="Add a custom cuisine…"
          maxLength={32}
          data-testid={`${testid}-custom-input`}
          className={
            dark
              ? 'flex-1 max-w-[220px] bg-white/[0.04] border border-white/12 focus:border-yellow-400 rounded-lg px-3 py-1.5 text-[12px] text-white/90 placeholder:text-white/35 outline-none transition-colors'
              : 'flex-1 max-w-[220px] bg-white border border-slate-200 focus:border-gold-500 rounded-lg px-3 py-1.5 text-[12px] text-slate-900 placeholder:text-slate-400 outline-none transition-colors'
          }
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={!custom.trim() || selected.length >= max}
          data-testid={`${testid}-custom-add`}
          className={
            dark
              ? 'inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/15 hover:border-white/30 text-white/85 text-[12px] font-bold disabled:opacity-40 transition-colors'
              : 'inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-gold-400 text-slate-700 text-[12px] font-bold disabled:opacity-40 transition-colors'
          }
        >
          <Plus className="w-3 h-3" />
          Add
        </button>
      </div>
      <p className={`text-[11px] mt-2 ${dark ? 'text-white/45' : 'text-slate-400'}`} data-testid={`${testid}-hint`}>
        Pick up to {max} cuisines. Customers use these to find your restaurant in search & filters.
      </p>
    </div>
  );
};

export default CuisineTypeSelector;
